/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-08
 */

import React from 'react';
import {
  Box,
  Container,
  CircularProgress,
  Alert,
  Typography,
  Button,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';

// Add local dependencies.
import Header from '../../layout/Header';
import { StudioHeader } from '../../navigation/studio';
import {
  useGetStudioQuery,
  useUpdateStudioMutation,
  useDeleteStudioMutation,
} from '../../../features/studios/studiosApi';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import LiveRegion from '../../common/LiveRegion';
import { ARIA_ROLES } from '../../../utils/accessibility';
import { StudioEditForm } from '../../studios/forms';
import { StudioFormData } from '../../../types/studioForms';
import { StudioDeleteDialog } from '../../studios/forms/StudioDeleteDialog';
import { useSuccessNotification } from '../../../hooks/useSuccessNotification';

/**
 * Studio settings page component.
 * Displays and manages studio settings and configuration.
 * Only accessible to users with edit studio permissions.
 *
 * @example
 * return (
 *   <SettingsPage />
 * )
 * @returns {React.ReactElement} component.
 */
const SettingsPage: React.FC = () => {
  const { studioId } = useParams<{ studioId: string }>();
  const navigate = useNavigate();

  // Fetch studio data
  const {
    data: studio,
    isLoading: isLoadingStudio,
    error: studioError,
  } = useGetStudioQuery(studioId!, {
    skip: !studioId,
  });

  // Update studio mutation
  const [updateStudio, { isLoading: isUpdating, error: updateError }] =
    useUpdateStudioMutation();

  // Delete studio mutation
  const [deleteStudio, { isLoading: isDeleting }] = useDeleteStudioMutation();

  // Success notification hook
  const { showSuccess } = useSuccessNotification();

  // Check permissions
  const {
    permissions,
    role,
    isLoading: isLoadingPermissions,
  } = useStudioPermissions();

  // State for delete dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
    React.useState<boolean>(false);

  // Handle loading states
  if (isLoadingStudio || isLoadingPermissions) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader loading />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Loading studio settings, please wait..."
            politeness="POLITE"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '50vh',
              gap: 2,
            }}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.secondary">
              Loading settings...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Handle errors
  if (studioError || !studio) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader studio={null} />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Error loading studio information. Please try refreshing the page."
            politeness="ASSERTIVE"
          />
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {studioError &&
            'status' in studioError &&
            studioError.status === 404
              ? 'Studio not found. Please check the URL and try again.'
              : 'Failed to load studio information. Please try refreshing the page.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  // Check if user has permission to edit studio
  if (!permissions?.canEditStudio) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader
          studio={studio}
          userRole={role}
          permissions={permissions}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Access denied. You don't have permission to edit studio settings."
            politeness="ASSERTIVE"
          />
          <Alert severity="warning" sx={{ mb: 4 }} role="alert">
            You don&apos;t have permission to edit settings for this studio.
            Only studio owners and administrators can modify studio settings.
          </Alert>
        </Container>
      </Box>
    );
  }

  // Handle form submission
  const handleSubmit = async (formData: Partial<StudioFormData>) => {
    try {
      // Convert form data to match UpdateStudioRequest type
      const updateData: Partial<StudioFormData> = { ...formData };

      // Handle logo: convert null to undefined for API compatibility
      if (updateData.logo === null) {
        delete updateData.logo;
      }

      // Use the updateStudio mutation directly (now handles logo uploads via FormData)
      await updateStudio({
        id: studioId!,
        data: updateData as any, // Type assertion since we handle the logo conversion above
      }).unwrap();

      // Navigate back to studio overview on successful update
      navigate(`/studio/${studioId}`);
    } catch (error) {
      // Error is handled by the form component
      console.error('Failed to update studio:', error);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(`/studio/${studioId}`);
  };

  /**
   * Opens the delete studio confirmation dialog.
   */
  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  /**
   * Closes the delete studio confirmation dialog.
   */
  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  /**
   * Handles the studio deletion after user confirmation.
   * Shows success notification and redirects to studios list.
   */
  const handleDeleteConfirm = async () => {
    try {
      await deleteStudio(studioId!).unwrap();

      // Close dialog
      setIsDeleteDialogOpen(false);

      // Show success notification
      showSuccess({
        message: 'Studio deleted successfully',
        successType: 'general',
        hideDelay: 3000,
      });

      // Navigate to studios list
      navigate('/studios');
    } catch (error) {
      // Error is handled by the dialog component
      console.error('Failed to delete studio:', error);
    }
  };

  // Format error message for display
  const getErrorMessage = () => {
    if (updateError) {
      if ('data' in updateError && updateError.data) {
        if (typeof updateError.data === 'string') {
          return updateError.data;
        }
        if (
          typeof updateError.data === 'object' &&
          'message' in updateError.data
        ) {
          return updateError.data.message as string;
        }
      }
      return 'Failed to update studio settings. Please try again.';
    }

    return null;
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <StudioHeader studio={studio} userRole={role} permissions={permissions} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Live region for successful data load */}
        <LiveRegion
          message={`Studio settings loaded successfully for ${studio.name}`}
          politeness="POLITE"
        />

        {/* Page Content */}
        <Box
          role={ARIA_ROLES.MAIN}
          aria-label="Studio settings management"
          sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {/* Page Header */}
          <Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Studio Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage settings and configuration for {studio.name}
            </Typography>
          </Box>

          {/* Studio Edit Form */}
          <StudioEditForm
            studio={studio}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={isUpdating}
            error={getErrorMessage()}
          />

          {/* Delete Studio Section - Only visible to owners */}
          {role === 'owner' && (
            <Box
              sx={{
                mt: 4,
                pt: 4,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom>
                Danger Zone
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Once you delete a studio, there is no going back. Please be
                certain.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                disabled={isDeleting}
                aria-label="Delete studio permanently"
              >
                Delete Studio
              </Button>
            </Box>
          )}
        </Box>
      </Container>

      {/* Delete Studio Dialog */}
      <StudioDeleteDialog
        open={isDeleteDialogOpen}
        studio={studio}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />
    </Box>
  );
};

// Export component.
export default SettingsPage;
